import React, { useState } from 'react';
import { useAuth } from './AuthContext';
import { CREATOR_SIGNUPS_ENABLED } from './config';
import './WelcomePage.css';

function WelcomePage({ onContinue }) {
  const { signInWithGoogle, signUpWithEmail, signInWithEmail, signInWithTwitch, signInWithTikTok } = useAuth();
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [signingInAs, setSigningInAs] = useState('user'); // 'user' or 'creator'
  const [selectedPlatform, setSelectedPlatform] = useState(null); // 'tiktok' or 'instagram' for manual onboarding
  const [error, setError] = useState('');
  const [isSignUp, setIsSignUp] = useState(true); // Toggle between sign up and sign in
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSignInClick = (type) => {
    console.log('Opening sign-in modal as:', type);
    setSigningInAs(type);
    setSelectedPlatform(null); // Reset platform selection
    setShowSignInModal(true);
    setError('');
  };

  const handlePlatformSignIn = (platform) => {
    console.log('Selected platform for manual onboarding:', platform);
    setSelectedPlatform(platform);
    setSigningInAs('creator');
    setShowSignInModal(true);
    setError('');
  };

  const handleGoogleSignIn = async () => {
    try {
      setLoading(true);
      setError('');
      const isCreator = signingInAs === 'creator';
      await signInWithGoogle(isCreator, isCreator);
      onContinue();
    } catch (error) {
      console.error('Sign-in error:', error);
      setError(error.message || 'Failed to sign in with Google. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      setError('Please enter both email and password.');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      
      if (isSignUp) {
        const isCreator = signingInAs === 'creator';
        await signUpWithEmail(email, password, isCreator);
        setError('Account created! Please check your email to verify your account.');
        setTimeout(() => {
          onContinue();
        }, 2000);
      } else {
        await signInWithEmail(email, password);
        onContinue();
      }
    } catch (error) {
      console.error('Email auth error:', error);
      setError(error.message || 'Authentication failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleTwitchSignIn = async () => {
    try {
      setLoading(true);
      setError('');
      await signInWithTwitch();
      // Twitch auth will redirect, so onContinue will be called after redirect
    } catch (error) {
      console.error('Sign-in error:', error);
      setError(error.message || 'Failed to sign in with Twitch. Please try again.');
      setLoading(false);
    }
  };

  const handleTikTokSignIn = async () => {
    try {
      setLoading(true);
      setError('');
      await signInWithTikTok();
      // TikTok auth will redirect, so onContinue will be called after redirect
    } catch (error) {
      console.error('Sign-in error:', error);
      setError(error.message || 'Failed to sign in with TikTok. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="welcome-container">
      <div className="welcome-content">
        <h1 className="welcome-title">
          <span className="title-the">The</span>
          <span className="title-subgames">SubGames</span>
        </h1>

        <div className="welcome-buttons">
          <button 
            onClick={() => handleSignInClick('user')} 
            className="btn-player"
          >
            Sign In as Player
          </button>

          {CREATOR_SIGNUPS_ENABLED ? (
            <button 
              onClick={() => handleSignInClick('creator')} 
              className="btn-creator"
            >
              Sign In as Creator
            </button>
          ) : (
            <div style={{
              padding: '16px',
              background: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '12px',
              textAlign: 'center',
              color: 'rgba(255, 255, 255, 0.7)'
            }}>
              <p style={{ margin: 0, fontSize: '14px' }}>Creator signups temporarily closed</p>
            </div>
          )}
        </div>
      </div>

      {/* Sign-In Modal */}
      {showSignInModal && (
        <div className="modal-overlay" onClick={() => setShowSignInModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button 
              className="modal-close" 
              onClick={() => setShowSignInModal(false)}
            >
              ×
            </button>
            
            <h2>Sign In as {signingInAs === 'creator' ? 'Creator' : 'Player'}</h2>
            <p className="modal-subtitle">
              {selectedPlatform === 'tiktok' 
                ? 'Sign in to create your TikTok creator profile' 
                : selectedPlatform === 'instagram'
                ? 'Sign in to create your Instagram creator profile'
                : signingInAs === 'creator' 
                ? 'Sign in with your creator platform for verified access' 
                : 'Choose your sign-in method'}
            </p>

            {error && <div className="error-message">{error}</div>}

            <div className="sign-in-buttons">
              {signingInAs === 'creator' && !selectedPlatform ? (
                <>
                  {/* Platform-specific sign-in for creators */}
                  <button 
                    onClick={handleGoogleSignIn} 
                    className="btn-youtube"
                    disabled={loading}
                    style={{
                      backgroundColor: '#FF0000',
                      color: 'white',
                      border: 'none',
                      padding: '12px 24px',
                      borderRadius: '8px',
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      marginBottom: '12px',
                      width: '100%'
                    }}
                  >
                    <i className="fab fa-youtube" style={{marginRight: '8px'}}></i>
                    {loading ? 'Signing in...' : 'Sign in with YouTube'}
                  </button>

                  <button 
                    onClick={handleTwitchSignIn} 
                    className="btn-twitch"
                    disabled={loading}
                    style={{
                      backgroundColor: '#9146FF',
                      color: 'white',
                      border: 'none',
                      padding: '12px 24px',
                      borderRadius: '8px',
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      marginBottom: '12px',
                      width: '100%'
                    }}
                  >
                    <i className="fab fa-twitch" style={{marginRight: '8px'}}></i>
                    {loading ? 'Signing in...' : 'Sign in with Twitch'}
                  </button>

                  <button 
                    onClick={handlePlatformSignIn.bind(null, 'tiktok')} 
                    className="btn-tiktok"
                    disabled={loading}
                    style={{
                      backgroundColor: '#000000',
                      color: 'white',
                      border: 'none',
                      padding: '12px 24px',
                      borderRadius: '8px',
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      marginBottom: '12px',
                      width: '100%'
                    }}
                  >
                    <i className="fab fa-tiktok" style={{marginRight: '8px'}}></i>
                    {loading ? 'Signing in...' : 'Sign in as TikTok Creator'}
                  </button>

                  <button 
                    onClick={handlePlatformSignIn.bind(null, 'instagram')} 
                    className="btn-instagram"
                    disabled={loading}
                    style={{
                      backgroundColor: '#E4405F',
                      color: 'white',
                      border: 'none',
                      padding: '12px 24px',
                      borderRadius: '8px',
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      marginBottom: '12px',
                      width: '100%'
                    }}
                  >
                    <i className="fab fa-instagram" style={{marginRight: '8px'}}></i>
                    {loading ? 'Signing in...' : 'Sign in as Instagram Creator'}
                  </button>
                </>
              ) : selectedPlatform ? (
                <>
                  {/* Manual sign-in for TikTok/Instagram creators */}
                  <div style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    padding: '12px',
                    borderRadius: '8px',
                    marginBottom: '16px',
                    fontSize: '14px',
                    textAlign: 'center'
                  }}>
                    <i className="fas fa-info-circle" style={{marginRight: '8px'}}></i>
                    You'll complete your {selectedPlatform === 'tiktok' ? 'TikTok' : 'Instagram'} profile setup after signing in
                  </div>

                  <button 
                    onClick={handleGoogleSignIn} 
                    className="btn-google"
                    disabled={loading}
                  >
                    <i className="fab fa-google" style={{marginRight: '8px'}}></i>
                    {loading ? 'Signing in...' : 'Continue with Google'}
                  </button>

                  <form onSubmit={handleEmailAuth} style={{marginTop: '12px'}}>
                    <input
                      type="email"
                      placeholder="Email address"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="email-input"
                      disabled={loading}
                      required
                      style={{marginBottom: '8px'}}
                    />
                    <input
                      type="password"
                      placeholder="Password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="email-input"
                      disabled={loading}
                      required
                      minLength="6"
                      style={{marginBottom: '8px'}}
                    />
                    <button 
                      type="submit"
                      className="btn-email-modern"
                      disabled={loading}
                      style={{marginBottom: '8px'}}
                    >
                      <svg className="btn-icon-svg" viewBox="0 0 24 24" width="20" height="20" fill="white">
                        <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                      </svg>
                      {loading ? 'Processing...' : (isSignUp ? 'Sign Up' : 'Sign In')}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setIsSignUp(!isSignUp); setError(''); }}
                      className="toggle-auth-mode"
                      disabled={loading}
                    >
                      {isSignUp ? 'Already have an account?' : 'Need an account?'}
                    </button>
                  </form>
                </>
              ) : (
                <>
                  {/* General sign-in for players */}
                  <button 
                    onClick={handleGoogleSignIn} 
                    className="btn-google-modern"
                    disabled={loading}
                  >
                    <svg className="btn-icon-svg" viewBox="0 0 24 24" width="20" height="20">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    {loading ? 'Signing in...' : 'Continue with Google'}
                  </button>

                  <div className="divider">
                    <span>or</span>
                  </div>

                  <form onSubmit={handleEmailAuth} className="email-form">
                    <input
                      type="email"
                      placeholder="Email address"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="email-input"
                      disabled={loading}
                      required
                    />
                    <input
                      type="password"
                      placeholder="Password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="email-input"
                      disabled={loading}
                      required
                      minLength="6"
                    />
                    <button 
                      type="submit"
                      className="btn-email-modern"
                      disabled={loading}
                    >
                      <svg className="btn-icon-svg" viewBox="0 0 24 24" width="20" height="20" fill="white">
                        <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                      </svg>
                      {loading ? 'Processing...' : (isSignUp ? 'Sign Up with Email' : 'Sign In with Email')}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setIsSignUp(!isSignUp); setError(''); }}
                      className="toggle-auth-mode"
                      disabled={loading}
                    >
                      {isSignUp ? 'Already have an account? Sign in' : 'Need an account? Sign up'}
                    </button>
                  </form>
                </>
              )}
            </div>

            <p className="terms-text">
              By signing in, you agree to our{' '}
              <a href="/terms-of-service.html" target="_blank" rel="noopener noreferrer" style={{ color: '#60a5fa', textDecoration: 'underline' }}>
                Terms of Service
              </a>{' '}
              and{' '}
              <a href="/privacy-policy.html" target="_blank" rel="noopener noreferrer" style={{ color: '#60a5fa', textDecoration: 'underline' }}>
                Privacy Policy
              </a>
            </p>
          </div>
        </div>
      )}
      
      {/* Legal Footer */}
      <div style={{
        position: 'fixed',
        bottom: '10px',
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        gap: '15px',
        fontSize: '12px',
        color: 'rgba(255, 255, 255, 0.5)',
        zIndex: 1
      }}>
        <a 
          href="/privacy-policy.html" 
          target="_blank" 
          rel="noopener noreferrer"
          style={{ color: 'rgba(255, 255, 255, 0.5)', textDecoration: 'none' }}
        >
          Privacy
        </a>
        <span>•</span>
        <a 
          href="/terms-of-service.html" 
          target="_blank" 
          rel="noopener noreferrer"
          style={{ color: 'rgba(255, 255, 255, 0.5)', textDecoration: 'none' }}
        >
          Terms
        </a>
      </div>
    </div>
  );
}

export default WelcomePage;
